(() => {
  const cfg = window.__FREE_GIFT__;
  if (!cfg || !cfg.enabled) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const CART_JSON = `${root}cart.js`;
  const ADD_URL = `${root}cart/add.js`;
  const CHANGE_URL = `${root}cart/change.js`;

  const qualifyingSet = new Set((cfg.qualifyingProductIds || []).map((x) => Number(x)));
  const triggerMode = String(cfg.triggerMode || "any").toLowerCase(); // any | all
  const minQtyEach = Math.max(1, Number(cfg.minQtyEach || 2));
  const qtyMode = String(cfg.qtyMode || "fixed_1").toLowerCase(); // fixed_1 | per_trigger

  let isProcessing = false;
  let debounceTimer = null;
  let lastRunAt = 0;

  // controla ondas para não empilhar infinito
  let waveTimers = [];

  const debounce = (fn, wait = 120) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fn, wait);
  };

  const fetchCart = async () => {
    const res = await fetch(CART_JSON, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  };

  const postJSON = async (url, body) => {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Cart request failed: ${res.status} ${txt}`);
    }
    return res.json().catch(() => ({}));
  };

  const getDrawer = () => document.querySelector("m-cart-drawer");

  const refreshMinimogCartUI = () => {
    const drawer = getDrawer();
    if (drawer && typeof drawer.onCartDrawerUpdate === "function") {
      drawer.onCartDrawerUpdate(true);
    }
  };

  const lockGiftQtyUI = () => {
    const gProdId = String(cfg.giftProductId);
    const candidates = document.querySelectorAll(
      `[data-product-id="${gProdId}"], [data-cart-item-product-id="${gProdId}"]`
    );

    candidates.forEach((rootEl) => {
      rootEl.classList.add("is-free-gift");

      rootEl.querySelectorAll('input[name="updates[]"], input[type="number"]').forEach((inp) => {
        inp.setAttribute("disabled", "disabled");
        inp.setAttribute("readonly", "readonly");
      });

      rootEl
        .querySelectorAll(
          'button[name="minus"], button[name="plus"], .m-quantity__button, .quantity__button, [data-qty-minus], [data-qty-plus]'
        )
        .forEach((btn) => {
          btn.setAttribute("disabled", "disabled");
          btn.setAttribute("aria-disabled", "true");
          btn.style.pointerEvents = "none";
        });
    });
  };

  // ====== TRIGGERS (packs) ======
  const getTriggerQtyMap = (cart) => {
    const map = new Map();
    qualifyingSet.forEach((pid) => map.set(pid, 0));
    for (const item of cart.items || []) {
      const pid = Number(item.product_id);
      if (qualifyingSet.has(pid)) {
        map.set(pid, (map.get(pid) || 0) + Number(item.quantity || 0));
      }
    }
    return map;
  };

  const getTriggerPacksMap = (cart) => {
    const qtyMap = getTriggerQtyMap(cart);
    const packsMap = new Map();
    for (const [pid, qty] of qtyMap.entries()) {
      packsMap.set(pid, Math.floor(Number(qty || 0) / minQtyEach));
    }
    return packsMap;
  };

  const isQualified = (cart) => {
    const packsMap = getTriggerPacksMap(cart);

    if (triggerMode === "all") {
      for (const packs of packsMap.values()) {
        if (packs < 1) return false;
      }
      return true;
    }

    for (const packs of packsMap.values()) {
      if (packs >= 1) return true;
    }
    return false;
  };

  const desiredGiftQty = (cart) => {
    if (!isQualified(cart)) return 0;

    if (qtyMode === "per_trigger") {
      const packsMap = getTriggerPacksMap(cart);
      let total = 0;
      for (const packs of packsMap.values()) total += Number(packs || 0);
      return Math.max(1, total);
    }

    return 1;
  };

  const findGiftItem = (cart) => {
    const gProdId = Number(cfg.giftProductId);
    const gVarId = Number(cfg.giftVariantId);
    return (cart.items || []).find((it) => Number(it.product_id) === gProdId || Number(it.variant_id) === gVarId);
  };

  const ensureGift = async () => {
    await postJSON(ADD_URL, {
      items: [
        {
          id: Number(cfg.giftVariantId),
          quantity: 1,
          properties: { _free_gift: "true" },
        },
      ],
    });
  };

  const setGiftQty = async (giftItem, qty) => {
    await postJSON(CHANGE_URL, { id: giftItem.key, quantity: Number(qty) });
  };

  const syncFreeGift = async () => {
    const now = Date.now();
    if (now - lastRunAt < 180) return;
    lastRunAt = now;

    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await fetchCart();
      const wantQty = desiredGiftQty(cart);
      const giftItem = findGiftItem(cart);

      if (wantQty === 0) {
        if (giftItem) {
          await setGiftQty(giftItem, 0);
        }
        return;
      }

      if (!giftItem) {
        await ensureGift();
        // depois de inserir, ajusta qty se precisar > 1
        if (wantQty > 1) {
          const cart2 = await fetchCart();
          const gift2 = findGiftItem(cart2);
          if (gift2) await setGiftQty(gift2, wantQty);
        }
        return;
      }

      const currentQty = Number(giftItem.quantity || 0);
      if (currentQty !== wantQty) {
        await setGiftQty(giftItem, wantQty);
      }
    } catch (e) {
      console.error("[FreeGift] Sync error:", e);
    } finally {
      isProcessing = false;
    }
  };

  // ✅ Ondas pós-render do drawer (resolve o “só aparece ao recarregar”)
  const clearWaves = () => {
    waveTimers.forEach((t) => clearTimeout(t));
    waveTimers = [];
  };

  const runWaves = () => {
    clearWaves();

    const delays = [250, 650, 1200]; // ondas (pós render Minimog)
    delays.forEach((d) => {
      const t = setTimeout(async () => {
        try {
          await syncFreeGift();
        } catch (_) {}
        refreshMinimogCartUI();
        setTimeout(lockGiftQtyUI, 120);
      }, d);
      waveTimers.push(t);
    });
  };

  // ===== HOOKS =====

  document.addEventListener("DOMContentLoaded", () => {
    runWaves();
  });

  // quando o tema atualiza carrinho (quantidade/remove)
  document.addEventListener("cart:updated", () => {
    debounce(runWaves, 80);
  });

  // quando abre o drawer (evento do Minimog)
  try {
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents?.openCartDrawer) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.openCartDrawer, () => {
        runWaves();
      });
    }
  } catch (_) {}

  // clique no ícone do carrinho
  document.addEventListener("click", (e) => {
    if (e.target.closest(".m-cart-icon-bubble")) {
      runWaves();
    }
  });

  // ✅ Add-to-cart (principalmente carrinho vazio)
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const isATCForm =
        form.matches('[data-type="add-to-cart-form"]') ||
        !!form.querySelector('input[name="id"]') ||
        !!form.closest("product-form");

      if (!isATCForm) return;

      runWaves();
    },
    true
  );

  // BFCache / voltar da PDP pra coleção
  window.addEventListener("pageshow", () => {
    debounce(runWaves, 80);
  });
})();